/**
 * Swiss/EU Bundles page with high-conversion offers
 */

import * as React from 'react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EUBreadcrumbs from '@/components/navigation/EUBreadcrumbs';
import EnhancedLeadForm from '@/components/conversion/EnhancedLeadForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Check, 
  Star, 
  Clock, 
  Euro, 
  Banknote,
  Rocket,
  ShoppingCart,
  Crown,
  Globe,
  ArrowRight,
  Download,
  Play
} from 'lucide-react';
import { updateEnhancedPageSEO } from '@/utils/seoOptimization';
import type { SwissLanguage } from '@/utils/languageTypes';

const BundlesPage: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const [selectedCurrency, setSelectedCurrency] = useState<'CHF' | 'EUR'>('CHF');
  const [selectedBundle, setSelectedBundle] = useState<string | null>(null);

  // Detect if EU page based on URL
  const isEUPage = location.pathname.includes('/eu/') || 
                   ['stuttgart', 'munich', 'lyon', 'paris', 'milan', 'como'].some(city => 
                     location.pathname.includes(city)
                   );

  React.useEffect(() => {
    if (isEUPage) {
      setSelectedCurrency('EUR');
    }
  }, [isEUPage]);

  const translations = {
    de: {
      pageTitle: 'Swiss/EU Digital Bundles',
      pageSubtitle: 'Komplettlösungen für Ihr digitales Wachstum',
      pageDescription: 'Entdecken Sie unsere bewährten Bundle-Pakete für nachhaltiges Wachstum.',
      
      launchBundle: 'Launch Bundle',
      launchSubtitle: 'KMU Starter Paket',
      launchDescription: 'Perfekt für den digitalen Start',
      
      ecommerceBundle: 'E-Commerce Scale Bundle',
      ecommerceSubtitle: 'Verkäufe & Conversions steigern',
      ecommerceDescription: 'Für Online-Shops mit Wachstumsambitionen',
      
      prestigeBundle: 'Prestige Visuals Bundle',
      prestigeSubtitle: 'Luxury & Premium Brands',
      prestigeDescription: 'Exklusive visuelle Erlebnisse',
      
      included: 'Enthalten',
      timeline: 'Umsetzung',
      price: 'Preis',
      addOns: 'Zusatzoptionen',
      faq: 'FAQ',
      getBundle: 'Bundle anfragen',
      viewDetails: 'Details ansehen'
    },
    en: {
      pageTitle: 'Swiss/EU Digital Bundles',
      pageSubtitle: 'Complete solutions for your digital growth',
      pageDescription: 'Discover our proven bundle packages for sustainable growth.',
      
      launchBundle: 'Launch Bundle',
      launchSubtitle: 'SME Starter Package',
      launchDescription: 'Perfect for digital beginnings',
      
      ecommerceBundle: 'E-Commerce Scale Bundle',
      ecommerceSubtitle: 'Boost Sales & Conversions',
      ecommerceDescription: 'For online stores with growth ambitions',
      
      prestigeBundle: 'Prestige Visuals Bundle',
      prestigeSubtitle: 'Luxury & Premium Brands',
      prestigeDescription: 'Exclusive visual experiences',
      
      included: 'Included',
      timeline: 'Timeline',
      price: 'Price',
      addOns: 'Add-ons',
      faq: 'FAQ',
      getBundle: 'Get Bundle',
      viewDetails: 'View Details'
    },
    fr: {
      pageTitle: 'Bundles Digitaux Suisse/UE',
      pageSubtitle: 'Solutions complètes pour votre croissance digitale',
      pageDescription: 'Découvrez nos packages éprouvés pour une croissance durable.',
      
      launchBundle: 'Bundle Launch',
      launchSubtitle: 'Package PME Starter',
      launchDescription: 'Parfait pour débuter en digital',
      
      ecommerceBundle: 'Bundle E-Commerce Scale',
      ecommerceSubtitle: 'Booster Ventes & Conversions',
      ecommerceDescription: 'Pour boutiques en ligne ambitieuses',
      
      prestigeBundle: 'Bundle Prestige Visuals',
      prestigeSubtitle: 'Marques Luxe & Premium',
      prestigeDescription: 'Expériences visuelles exclusives',
      
      included: 'Inclus',
      timeline: 'Délai',
      price: 'Prix',
      addOns: 'Options',
      faq: 'FAQ',
      getBundle: 'Demander Bundle',
      viewDetails: 'Voir Détails'
    },
    it: {
      pageTitle: 'Bundle Digitali Svizzera/UE',
      pageSubtitle: 'Soluzioni complete per la vostra crescita digitale',
      pageDescription: 'Scoprite i nostri pacchetti testati per una crescita sostenibile.',
      
      launchBundle: 'Bundle Launch',
      launchSubtitle: 'Pacchetto PMI Starter',
      launchDescription: 'Perfetto per iniziare in digitale',
      
      ecommerceBundle: 'Bundle E-Commerce Scale',
      ecommerceSubtitle: 'Aumenta Vendite & Conversioni',
      ecommerceDescription: 'Per negozi online con ambizioni di crescita',
      
      prestigeBundle: 'Bundle Prestige Visuals',
      prestigeSubtitle: 'Brand Lusso & Premium',
      prestigeDescription: 'Esperienze visive esclusive',
      
      included: 'Incluso',
      timeline: 'Tempistica',
      price: 'Prezzo',
      addOns: 'Opzioni Aggiuntive',
      faq: 'FAQ',
      getBundle: 'Richiedi Bundle',
      viewDetails: 'Vedi Dettagli'
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const bundleData = {
    launch: {
      icon: Rocket,
      color: 'from-blue-500 to-cyan-500',
      price: { CHF: '6,900 - 9,900', EUR: '7,100 - 10,200' },
      timeline: '14-21 Tage',
      features: [
        'Responsive Website (5-8 Seiten)',
        'Multilingual Setup (DE/FR/IT/EN)',
        'Basic SEO Optimierung',
        'Cost Calculator Integration',
        'GDPR/revFADP Compliance',
        'Analytics & Tracking Setup',
        'SSL & Performance Optimization',
        '2 Revision Rounds'
      ],
      addOns: [
        'E-Commerce Modul: +CHF 2,500',
        'Blog System: +CHF 1,200',
        'Booking System: +CHF 1,800'
      ]
    },
    ecommerce: {
      icon: ShoppingCart,
      color: 'from-green-500 to-emerald-500',
      price: { CHF: '12,500 - 24,000', EUR: '12,900 - 24,700' },
      timeline: '21-35 Tage',
      features: [
        'Shopify/WooCommerce Setup',
        'CRO & A/B Testing Framework',
        'Google & Meta Ads Setup',
        'Email Marketing Flows',
        'Cart Abandonment Automation',
        'Product Feed Optimization',
        'Payment Gateway Integration',
        'Inventory Management',
        'Advanced Analytics'
      ],
      addOns: [
        'Monthly Retainer: CHF 2,500/m',
        'Advanced Automations: +CHF 3,500',
        'Multi-market Setup: +CHF 4,200'
      ]
    },
    prestige: {
      icon: Crown,
      color: 'from-purple-500 to-pink-500',
      price: { CHF: '18,000 - 60,000', EUR: '18,500 - 61,800' },
      timeline: '35-60 Tage',
      features: [
        '3D Product Visualization',
        'Motion Graphics Package',
        'AR Try-On Experience',
        'VR Showroom/Tour',
        'AI Influencer Campaign',
        'Premium Video Content',
        'Interactive Experiences',
        'White-Glove Service'
      ],
      addOns: [
        'Extended AR Features: +CHF 8,000',
        'Custom AI Avatar: +CHF 12,000',
        'Multi-platform VR: +CHF 15,000'
      ]
    }
  };

  // SEO setup
  React.useEffect(() => {
    updateEnhancedPageSEO({
      title: `${t.pageTitle} | Future Level`,
      description: t.pageDescription,
      currentPath: location.pathname,
      isSwissPage: !isEUPage,
      language: currentLanguage as SwissLanguage,
      service: 'bundles'
    });
  }, [currentLanguage, location.pathname, isEUPage, t.pageTitle, t.pageDescription]);

  const handleBundleSelect = (bundleKey: string) => {
    setSelectedBundle(bundleKey);
    // Scroll to form or open modal
    document.getElementById('bundle-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatPrice = (price: { CHF: string; EUR: string }) => {
    return selectedCurrency === 'CHF' ? `CHF ${price.CHF}` : `EUR ${price.EUR}`;
  };

  return (
    <>
      <Header />
      <EUBreadcrumbs />
      
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 to-primary-glow/5">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                {t.pageTitle}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                {t.pageSubtitle}
              </p>

              {/* Currency Toggle */}
              <div className="flex items-center justify-center space-x-4 mb-8">
                <Button
                  variant={selectedCurrency === 'CHF' ? 'default' : 'outline'}
                  onClick={() => setSelectedCurrency('CHF')}
                  className="flex items-center"
                >
                  <Banknote className="w-4 h-4 mr-2" />
                  CHF
                </Button>
                <Button
                  variant={selectedCurrency === 'EUR' ? 'default' : 'outline'}
                  onClick={() => setSelectedCurrency('EUR')}
                  className="flex items-center"
                >
                  <Euro className="w-4 h-4 mr-2" />
                  EUR
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Bundles Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8">
              {Object.entries(bundleData).map(([key, bundle], index) => {
                const Icon = bundle.icon;
                const isPopular = key === 'ecommerce';
                
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    {isPopular && (
                      <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground z-10">
                        <Star className="w-3 h-3 mr-1" />
                        Most Popular
                      </Badge>
                    )}
                    
                    <Card className={`h-full transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                      isPopular ? 'border-primary shadow-lg' : ''
                    }`}>
                      <CardHeader>
                        <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${bundle.color} flex items-center justify-center mb-4`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        
                        <CardTitle className="text-xl">
                          {key === 'launch' && t.launchBundle}
                          {key === 'ecommerce' && t.ecommerceBundle}
                          {key === 'prestige' && t.prestigeBundle}
                        </CardTitle>
                        
                        <p className="text-muted-foreground">
                          {key === 'launch' && t.launchDescription}
                          {key === 'ecommerce' && t.ecommerceDescription}
                          {key === 'prestige' && t.prestigeDescription}
                        </p>
                      </CardHeader>

                      <CardContent className="space-y-6">
                        {/* Price */}
                        <div className="text-center">
                          <div className="text-2xl font-bold text-primary">
                            {formatPrice(bundle.price)}
                          </div>
                          <div className="flex items-center justify-center text-sm text-muted-foreground mt-1">
                            <Clock className="w-4 h-4 mr-1" />
                            {bundle.timeline}
                          </div>
                        </div>

                        <Separator />

                        {/* Features */}
                        <div>
                          <h4 className="font-semibold mb-3">{t.included}:</h4>
                          <ul className="space-y-2">
                            {bundle.features.map((feature, index) => (
                              <li key={index} className="flex items-start">
                                <Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                <span className="text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <Separator />

                        {/* Add-ons */}
                        <div>
                          <h4 className="font-semibold mb-3">{t.addOns}:</h4>
                          <ul className="space-y-1">
                            {bundle.addOns.map((addon, index) => (
                              <li key={index} className="text-sm text-muted-foreground">
                                • {addon}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <Button 
                          onClick={() => handleBundleSelect(key)}
                          className={`w-full ${isPopular ? 'neo-button' : ''}`}
                          variant={isPopular ? 'default' : 'outline'}
                        >
                          {t.getBundle}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Lead Form Section */}
        <section id="bundle-form" className="py-20 bg-card/30">
          <div className="container mx-auto px-4">
            <div className="max-w-lg mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">Kostenloses Konzept erhalten</h2>
                <p className="text-muted-foreground">
                  Lassen Sie uns Ihr Projekt in einem 1-seitigen Konzept skizzieren.
                </p>
              </div>
              
              <EnhancedLeadForm
                prefilledService="webdesign"
                variant="inline"
                onSuccess={(lead) => {
                  console.log('Bundle lead submitted:', lead);
                }}
              />
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </>
  );
};

export default BundlesPage;